// ABOUTME: Material-UI avatar selection component with static image options and file upload
// ABOUTME: Fetches pre-made avatar images from backend API and supports custom uploads (issue #1016)

import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Avatar,
  IconButton,
  Alert,
  CircularProgress,
  Card,
  CardActionArea
} from '@mui/material';
import {
  PhotoCamera,
  CheckCircle
} from '@mui/icons-material';
import { getCurrentAccessToken } from '../services/api';

interface AvatarSelectorProps {
  selectedAvatar?: string;
  onSelect: (avatarUrl: string) => void;
}

const AvatarSelector: React.FC<AvatarSelectorProps> = ({ selectedAvatar, onSelect }) => {
  const [avatarUrls, setAvatarUrls] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchAvatars();
  }, []);

  const fetchAvatars = async () => {
    try {
      const token = getCurrentAccessToken();
      const response = await fetch(`${import.meta.env.VITE_API_URL || 'http://localhost:5001'}/api/family/static-avatars`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch avatars');
      }

      const urls = await response.json();
      setAvatarUrls(urls);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load avatars');
    } finally {
      setIsLoading(false);
    }
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setError('Please select an image file');
      return;
    }

    // Validate file size (max 10MB — must match ImageProcessingService.MaxFileSizeBytes)
    if (file.size > 10 * 1024 * 1024) {
      setError('Image must be smaller than 10MB');
      return;
    }

    try {
      setError('');
      
      // For now, create a local URL for the uploaded image
      // In production, you would upload this to a server or cloud storage
      const imageUrl = URL.createObjectURL(file);
      onSelect(imageUrl);
    } catch {
      setError('Failed to upload image');
    }
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 4 }}>
        <CircularProgress sx={{ mb: 2 }} />
        <Typography variant="body1">Loading avatars...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ py: 2 }}>
      <Typography variant="h6" component="h4" sx={{ mb: 2, fontWeight: 600 }}>
        Choose an Avatar
      </Typography>
      
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Avatar Grid */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 3 }}>
        {avatarUrls.map((url, index) => {
          // Convert relative URL to absolute URL for display
          const absoluteUrl = url.startsWith('http') ? url :
            `${import.meta.env.VITE_API_URL || 'http://localhost:5001'}${url}`;

          return (
            <Box key={index} sx={{ flexBasis: '30%', minWidth: 120 }}>
              <Card
                sx={{
                  position: 'relative',
                  cursor: 'pointer',
                  border: selectedAvatar === url ? 2 : 1,
                  borderColor: selectedAvatar === url ? 'primary.main' : 'divider',
                  '&:hover': {
                    borderColor: 'primary.main',
                    transform: 'scale(1.05)'
                  },
                  transition: 'all 0.2s ease'
                }}
                className={selectedAvatar === url ? 'Mui-selected' : ''}
              >
                <CardActionArea
                  onClick={() => onSelect(url)}
                  sx={{ p: 2, textAlign: 'center' }}
                >
                  <Avatar
                    src={absoluteUrl}
                    sx={{
                      width: 48,
                      height: 48,
                      mx: 'auto',
                      mb: 1
                    }}
                  />
                  {selectedAvatar === url && (
                    <CheckCircle
                      sx={{
                        position: 'absolute',
                        top: 8,
                        right: 8,
                        color: 'primary.main'
                      }}
                    />
                  )}
                </CardActionArea>
              </Card>
            </Box>
          );
        })}
        
        {/* Upload custom image option */}
        <Box sx={{ flexBasis: '30%', minWidth: 120 }}>
          <Card
            sx={{
              cursor: 'pointer',
              border: 1,
              borderColor: 'divider',
              '&:hover': {
                borderColor: 'primary.main',
                transform: 'scale(1.05)'
              },
              transition: 'all 0.2s ease'
            }}
          >
            <Box
              component="input"
              type="file"
              id="avatar-upload"
              accept="image/*"
              onChange={handleFileUpload}
              sx={{ display: 'none' }}
            />
            <Box component="label" htmlFor="avatar-upload" sx={{ display: 'block' }}>
              <CardActionArea sx={{ p: 2, textAlign: 'center' }}>
                <IconButton
                  component="span"
                  sx={{
                    width: 48,
                    height: 48,
                    mx: 'auto',
                    mb: 1,
                    backgroundColor: 'primary.light',
                    color: 'primary.contrastText',
                    '&:hover': {
                      backgroundColor: 'primary.main'
                    }
                  }}
                >
                  <PhotoCamera />
                </IconButton>
                <Typography variant="caption" sx={{ fontWeight: 600 }}>
                  Upload Photo
                </Typography>
              </CardActionArea>
            </Box>
          </Card>
        </Box>
      </Box>

      {/* Selected Avatar Preview */}
      {selectedAvatar && (
        <Card sx={{ p: 3, textAlign: 'center', backgroundColor: 'action.hover' }}>
          <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
            Selected Avatar:
          </Typography>
          <Avatar
            src={selectedAvatar.startsWith('http') || selectedAvatar.startsWith('/')
              ? (selectedAvatar.startsWith('http') ? selectedAvatar : `${import.meta.env.VITE_API_URL || 'http://localhost:5001'}${selectedAvatar}`)
              : selectedAvatar}
            sx={{
              width: 80,
              height: 80,
              mx: 'auto'
            }}
          />
        </Card>
      )}
    </Box>
  );
};

export default AvatarSelector;